// Notifications: the feed, acknowledgements and comments (spec 07).
import { postForm, request } from './api';
import type { Attachment } from './documentsApi';

export type AudienceMode = 'everyone' | 'picked';

export interface Post {
  id: string;
  body: string;
  authorId: string | null;
  audienceMode: AudienceMode;
  audienceUserIds: string[];
  requiresAck: boolean;
  job: { id: string; number: string; title: string } | null;
  version: number;
  edited: boolean;
  commentsLocked: boolean;
  archived: boolean;
  isSampleData: boolean;
  createdAt: string;
  updatedAt: string;
  seenCount: number;
  commentCount: number;
  /** Live files on the post — the feed draws a paperclip, not the files. */
  attachmentCount: number;
  acknowledgement: {
    audienceSize: number;
    acknowledgedCount: number;
    outstandingCount: number;
    requestedVersion: number;
  } | null;
  viewer: {
    inAudience: boolean;
    seen: boolean;
    acknowledged: boolean;
    canEdit: boolean;
    canLock: boolean;
  };
}

export interface PostComment {
  id: string;
  postId: string;
  authorId: string | null;
  body: string | null;
  deleted: boolean;
  createdAt: string;
  canDelete: boolean;
}

export interface Acknowledgement {
  userId: string;
  version: number;
  acknowledgedAt: string;
  current: boolean;
}

export interface Revision {
  version: number;
  body: string;
  editedById: string | null;
  createdAt: string;
}

/** Names for the ids in a response — see the server's `namesFor`. */
export interface Person {
  id: string;
  name: string;
}

export interface PostDetail {
  post: Post;
  comments: PostComment[];
  attachments: Attachment[];
  acknowledgements: Acknowledgement[];
  outstandingUserIds: string[];
  revisions: Revision[];
  people: Person[];
}

/** A lookup that degrades to something readable rather than a bare uuid. */
export function nameOf(people: Person[], id: string | null): string {
  if (id === null) return 'Someone no longer on the account';
  return people.find((person) => person.id === id)?.name ?? 'Someone else';
}

export const notifications = {
  feed: () => request<{ posts: Post[]; people: Person[]; today: string }>('/posts'),

  badge: () => request<{ unseen: number; outstanding: number }>('/posts/badge'),

  people: () =>
    request<{ people: (Person & { role: 'admin' | 'manager' | 'field' })[] }>('/posts/people'),

  get: (id: string) => request<PostDetail>(`/posts/${id}`),

  /**
   * Posts, with its files if there are any.
   *
   * ONE REQUEST, not a post followed by uploads (round 38). The push to the
   * crew is queued inside the server's create transaction and is due
   * immediately, so files that followed the post could land after the phones
   * had already buzzed — and an acknowledgement post has no way to say the
   * attachment arrived late. No files means the plain JSON path, unchanged.
   */
  create: (
    input: {
      body: string;
      audienceMode?: AudienceMode;
      audienceUserIds?: string[];
      requiresAck?: boolean;
      jobId?: string | null;
      files?: File[];
    },
    onProgress?: (fraction: number) => void
  ) => {
    const { files = [], ...body } = input;
    if (files.length === 0) {
      return request<{ post: Post }>('/posts', { method: 'POST', body });
    }

    const form = new FormData();
    form.append('body', body.body);
    if (body.audienceMode) form.append('audienceMode', body.audienceMode);
    // Repeated fields rather than a JSON array — see the server's
    // `createInput`. One append per id, so one person picked is not a
    // different shape from three.
    for (const id of body.audienceUserIds ?? []) form.append('audienceUserIds', id);
    if (body.requiresAck) form.append('requiresAck', 'true');
    if (body.jobId) form.append('jobId', body.jobId);
    for (const file of files) form.append('files', file, file.name);

    return postForm<{ post: Post }>('/posts', form, onProgress);
  },

  update: (
    id: string,
    body: {
      body?: string;
      audienceMode?: AudienceMode;
      audienceUserIds?: string[];
      requiresAck?: boolean;
      jobId?: string | null;
      reRequestAck?: boolean;
    }
  ) => request<{ post: Post }>(`/posts/${id}`, { method: 'PATCH', body }),

  remove: (id: string) => request<void>(`/posts/${id}`, { method: 'DELETE' }),

  lock: (id: string, locked: boolean) =>
    request<void>(`/posts/${id}/lock`, { method: 'POST', body: { locked } }),

  markSeen: (id: string) => request<void>(`/posts/${id}/seen`, { method: 'POST' }),

  acknowledge: (id: string) => request<void>(`/posts/${id}/acknowledge`, { method: 'POST' }),

  comment: (id: string, body: string) =>
    request<{ comment: PostComment }>(`/posts/${id}/comments`, { method: 'POST', body: { body } }),

  deleteComment: (postId: string, commentId: string) =>
    request<void>(`/posts/${postId}/comments/${commentId}`, { method: 'DELETE' }),
};
